import { pianoParser } from "../../../../../common/parameters";
import player from "../../../../player";
import piano from "../../../../piano";
import { parseDo } from "../../../Score/methods/changeDo";
import getScore from "./getScore";
import getTempo from "./getTempo";
import getKeyOffset from "./getKeyOffset";
import scrollToBar from "./scrollToBar";
import getBarsOrder from "./getBarsOrder";
import { IBar, IFlowData, IScore } from "../../../../../common/types";
import { INotationNote, INotationThis } from "./interfaces";
import { getAccidentals } from "../../../Score/score-display/utils";

const Clock = require("waaclock");

const getSolutionNotes = (score: IScore) => {
    const notes: (INotationNote | null)[] = [];
    const maxVoice = [0, 1, 2, 3, 4, 5].filter(v => score["bars" + (v || "")] as IBar[]).pop()!;
    const notesToLoad = new Set();
    const accidentals = getAccidentals(score);
    for (let v = 0; v <= maxVoice; v++) {
        const bars = score["bars" + (v || "")];
        for (let b = 0; b < bars.length; b++) {
            const doPos = parseDo(accidentals[b]);
            const bar = bars[b];
            if (bar === null || !bar.notes) continue;
            for (let n = 0; n < bar.notes.length; n++) {
                const note = bar.notes[n];
                if (note.r) {
                    notes.push({ rest: true });
                } else {
                    if (!note.p) {
                        notes.push(null);
                        continue;
                    }
                    let pianoNote =
                        60 +
                        pianoParser[note.p.s] +
                        (note.p.a || 0) +
                        getKeyOffset(doPos.value + 6) +
                        doPos.alt +
                        ((note.p.o || 0) + (score["octave" + v] || 0)) * 12;
                    notesToLoad.add(pianoNote);
                    let newNote = {
                        l: note.l,
                        d: note.d,
                        t: note.t,
                        piano: pianoNote,
                    };
                    if (note.d) {
                        newNote.d = true;
                    }
                    notes.push(newNote);
                }
            }
        }
    }
    return notes;
};

const getSolutionScore = (barsortingState: INotationThis["state"], score: IScore) => {
    const bars: (IBar | null)[] = [];
    const bars1: (IBar | null)[] = [];
    const solutions = barsortingState.solutions!;
    const dropBarRefs = barsortingState.dropBarRefs!;
    const randomOrder = barsortingState.randomOrder!;
    // TODO multiple voice
    solutions.forEach((solution, index) => {
        const solutionIndex = randomOrder[solution];
        if (dropBarRefs[index].v === 0) {
            bars[dropBarRefs[index].b] =
                dropBarRefs[solutionIndex].v === 0
                    ? score.bars[dropBarRefs[solutionIndex].b]
                    : score.bars1[dropBarRefs[solutionIndex].b];
        } else if (dropBarRefs[index].v === 1) {
            bars1[dropBarRefs[index].b] =
                dropBarRefs[solutionIndex].v === 0
                    ? score.bars[dropBarRefs[solutionIndex].b]
                    : score.bars1[dropBarRefs[solutionIndex].b];
        }
    });

    if (!bars[0]) bars[0] = null;
    if (!bars1[0]) bars1[0] = null;

    return {
        ...score,
        bars,
        bars1,
    };
};

const getRhytPlayType = (score: IScore) => {
    return score.bars.filter(bar => {
        if (!bar) return false;
        return (
            bar.notes.filter(note => {
                return note.r;
            }).length > 0
        );
    }).length > 0
        ? "piano"
        : "slap";
};

type IPoints = ({ time: number, nth: number, note?: number, bar?: number } | undefined)[][]

export function resetPlay(this: INotationThis, e: MouseEvent) {
    e.stopPropagation();
    this.pauseOffset = 0;
    this.setState({ playing: false });
};

export default async function (this: INotationThis, noFollow?: boolean, type?: string, playBar?: { v: number, b: number }, checkPlay?: boolean, playStyle?: "auto" | "slap" | "piano") {
    const { playing } = this.state;
    const { task, plan } = this.props;
    const notes = (task?.data as IFlowData)?.scores
        ? (this.state.notes[this.state.page!] as INotationNote[])
        : checkPlay
        ? getSolutionNotes(
              type === "barSorting2"
                  ? getSolutionScore(this.state, getScore(plan, task)!)
                  : type === "rhytNotation"
                  ? this.state.score!
                  : this.state.taskScore!
          )
        : this.state.notes;
    if (this.isCheckPlayPause !== (checkPlay || false) || playBar) {
        this.pauseOffset = 0;
    }
    if (playing) {
        if (type !== "rhytNotation") {
            this.pauseOffset = (Date.now() - this.startedAt!) / 1000 - 0.2;
            this.isCheckPlayPause = checkPlay || false;
            if (this.pauseOffset + 0.3 > this.willEndAt!) {
                this.pauseOffset = 0;
            }
        }
        window.removeEventListener("blur", this.blur);
        this.setState({ playing: false, checkPlaying: false, playingNote: [], currentPlayingBar: null });
        if (this.clock) {
            this.clock.stop();
        }
        piano.stop();
        delete this.clock;
        return;
    }
    if (!player.slap) {
        const success = await player.loadSlap();
        if (!success) return; // this.props.showNotification("Error: Failed to load audio!", "warning");
    }
    if (type === "rhytNotation") {
        const success = await piano.load(69);
        if (!success) return; // this.props.showNotification("Error: Failed to load audio!", "warning");
    }
    window.addEventListener("blur", this.blur);
    let t = [0, 0, 0, 0, 0, 0];
    let late = 0.2;
    this.clock = new Clock(player.context, { toleranceEarly: 0.1 });
    this.clock.start();
    const pauseOffset = this.pauseOffset || 0;
    const score = (this.scoreEdit
        ? this.state.score
        : checkPlay && type === "rhytNotation"
        ? this.state.score
        : checkPlay && type !== "barSorting2"
        ? this.state.taskScore
        : checkPlay && type === "barSorting2"
        ? getSolutionScore(this.state, getScore(plan, task)!)
        : !(task?.data as IFlowData)?.scores
        ? getScore(plan, task)
        : (task.data as IFlowData).scores[this.state.page!])!;
    const playNotes: {
        time: number,
        note: number | number[] | null | "slap" | "piano",
        duration: number,
        isBeat?: boolean,
    }[][]= [[], [], [], [], [], []];
    let tempo =
        this.state.tempo || this.props.tempo || ((task?.data as IFlowData).tempos ? (task.data as IFlowData).tempos![this.state.page!] || 60 : getTempo(plan, task));
    let nth = 0;
    const maxVoice = [0, 1, 2, 3, 4, 5].filter(v => score["bars" + (v || "")]).pop()!;
    const points: IPoints = [[], [], [], [], [], []];
    const barpoints: number[] = [];
    const playingNote = [-1, -1, -1, -1, -1, -1];
    const barsOrder = getBarsOrder(score, !!playBar);
    const firstNoteOfBars: number[][] = [[], [], [], [], [], []];
    const firstNoteOfBarsInVoice = [];
    let nthInVoice = 0;
    let rhytPlayType: "slap" | "piano" = "slap";
    if (type === "rhytNotation") {
        if (playStyle === "auto") {
            rhytPlayType = getRhytPlayType(score);
        } else {
            rhytPlayType = playStyle!;
        }
    }
    for (let v = 0; v <= (type === "singing" || type === "rhytNotation" ? 0 : maxVoice); v++) {
        let lastNote;
        nthInVoice = 0;
        const bars = score["bars" + (v || "")] as IBar[];
        for (let b of barsOrder) {
            let bar = bars[b];
            if (!bar) continue;
            if (!playBar) {
                if (firstNoteOfBars[v][b] !== undefined) {
                    nth = firstNoteOfBars[v][b];
                    nthInVoice = firstNoteOfBarsInVoice[b];
                } else {
                    firstNoteOfBars[v][b] = nth;
                    firstNoteOfBarsInVoice[b] = nthInVoice;
                }
            }
            if (type === "barSorting2" && !barpoints.includes(t[v])) {
                barpoints.push(t[v]);
                if (t[v] + late >= pauseOffset) {
                    this.clock.callbackAtTime(() => {
                        this.setState({ currentPlayingBar: b });
                    }, player.context.currentTime + t[v] + late - pauseOffset);
                }
            }
            if ((playBar && playBar.v !== v) || (playBar && playBar.b !== b)) {
                if (playBar.v === v) {
                    if (b === playBar.b + 1) {
                        if (t[playBar.v] + late >= pauseOffset) {
                            points[playBar.v][points[playBar.v].length] = {
                                time: player.context.currentTime + t[playBar.v] + late - pauseOffset,
                                nth,
                            };
                        }
                    } else {
                        if (points[playBar.v].length < points[playBar.v].length + bar.notes.length) {
                            points[playBar.v][points[playBar.v].length - 1 + bar.notes.length] = undefined;
                        }
                    }
                }
                nth += bar.notes.length;
                nthInVoice += bar.notes.length;
                continue;
            }
            for (let n = 0; n < bar.notes.length; n++) {
                let note = bar.notes[n];
                let noteLength = (4 / note.l) * (60 / tempo) * (note.d ? 1.5 : 1) * (note.t ? 0.67 : 1); // TODO getNoteLength method
                let prolong = 0;
                if (note.tie) {
                    let nextNote = bar.notes[n + 1];
                    if (!nextNote) {
                        nextNote = score["bars" + (v || "")]?.[b + 1]?.notes?.[0];
                    }
                    if (nextNote) {
                        prolong = (4 / nextNote.l) * (60 / tempo) * (nextNote.d ? 1.5 : 1) * (nextNote.t ? 0.67 : 1);
                    }
                }
                if (!note.r && (!lastNote || !lastNote.tie)) {
                    const note = (type === "rhytNotation" && !notes[nth]?.piano) ? (rhytPlayType === "piano" ? "piano" : undefined) : notes[nth] ? notes[nth]!.piano : null;
                    if (note) {
                        if (t[v] >= pauseOffset) {
                            playNotes[v].push({
                                time: t[v] - pauseOffset,
                                note,
                                duration: noteLength + prolong,
                                ...(n === 0 &&
                                    b !== 0 && {
                                        isBeat: true,
                                    }),
                            });
                        }
                    } else {
                        if (t[v] >= pauseOffset) {
                            playNotes[v].push({
                                time: t[v] - 0.13 - pauseOffset,
                                note: null,
                                duration: noteLength + prolong,
                                ...(n === 0 &&
                                    b !== 0 && {
                                        isBeat: true,
                                    }),
                            });
                        }
                    }
                }
                if (t[v] + late >= pauseOffset) {
                    points[v].push({
                        time: player.context.currentTime + t[v] + late - pauseOffset,
                        nth: nthInVoice,
                        note: n,
                        bar: b,
                    });
                }
                t[v] += noteLength;
                lastNote = note;
                nth++;
                nthInVoice++;
            }
        }
    }
    if (!noFollow) {
        const notesInGroups = getNotesInGroups(points);
        notesInGroups.forEach(notes => {
            this.clock.callbackAtTime(() => {
                const { playingNote = [] } = this.state;
                let isScrolling = false;
                let barToScroll: number | undefined;
                notes.forEach(({ voice, nth, note, bar }) => {
                    playingNote[voice] = nth;
                    if (note === 0) {
                        isScrolling = true;
                        barToScroll = bar!;
                    }
                });
                if (isScrolling && this.scoreRef && this.distribution && barToScroll) {
                    scrollToBar(this.distribution, this.scoreRef, barToScroll);
                }
                this.setState({ playingNote: [...playingNote] });
            }, notes[0].time);
        });
    }
    this.startedAt = Date.now() - pauseOffset * 1000;
    this.willEndAt = Math.max.apply(Math, t);
    this.clock.callbackAtTime(this.play, player.context.currentTime + Math.max.apply(Math, t) + late - pauseOffset);

    playNotes.forEach(voice => {
        piano.schedule(player.context.currentTime + late, voice);
    });
    if (this.props.handlePlayCount && !this.pauseOffset && !checkPlay) {
        this.props.handlePlayCount();
    }
    this.setState({
        playing: true,
        ...(checkPlay && { checkPlaying: true }),
        playingNote,
        points,
        playingStarted: false,
    });
}

type INotesInOrder = {
    time: number,
    nth: number,
    voice: number,
    note?: number,
    bar?: number,
}

const getNotesInGroups = (pointsByVoice: IPoints) => {
    let notesInOrder: INotesInOrder[] = [];
    pointsByVoice.forEach((points, voiceIndex) => {
        points.forEach(point => {
            if (!point) return;
            const { time, nth, note, bar } = point;
            notesInOrder.push({
                time,
                nth,
                voice: voiceIndex,
                note,
                bar,
            });
        });
    });
    notesInOrder = notesInOrder.sort((a, b) => a.time - b.time);
    let notesByTime: INotesInOrder[][] = [];
    notesInOrder.forEach(note => {
        if (
            notesByTime[notesByTime.length - 1] &&
            notesByTime[notesByTime.length - 1][0].time.toFixed(4) === note.time.toFixed(4)
        ) {
            notesByTime[notesByTime.length - 1].push(note);
        } else {
            notesByTime.push([note]);
        }
    });
    return notesByTime;
};
